import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, FileText, Upload, Image, X, Eye } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ value, onChange, label }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const getToken = () => localStorage.getItem('token');
  const imgSrc = value?.startsWith('/api') ? `${API_URL}${value}` : value;
  const CHUNK_SIZE = 4 * 1024 * 1024;

  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const initRes = await axios.post(`${API_URL}/api/upload/init`, {
      filename: file.name, total_chunks: totalChunks
    }, { headers: { Authorization: `Bearer ${getToken()}` } });
    const { upload_id } = initRes.data;
    for (let i = 0; i < totalChunks; i++) {
      const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append('file', chunk, `chunk_${i}`);
      await axios.post(`${API_URL}/api/upload/chunk/${upload_id}/${i}`, fd, {
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }, timeout: 60000
      });
    }
    return (await axios.post(`${API_URL}/api/upload/complete/${upload_id}`, {
      filename: file.name, total_chunks: totalChunks, content_type: file.type || 'image/jpeg'
    }, { headers: { Authorization: `Bearer ${getToken()}` } })).data;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      let result;
      if (file.size > CHUNK_SIZE) {
        result = await uploadChunked(file);
      } else {
        const fd = new FormData();
        fd.append('file', file);
        result = (await axios.post(`${API_URL}/api/upload`, fd, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        })).data;
      }
      onChange(result.url);
      toast.success('Imagine incarcata!');
    } catch (err) {
      toast.error('Eroare la incarcare');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-[#A3A3A3] text-sm mb-2">{label}</label>
      <div className="flex gap-3 items-start">
        {value && (
          <div className="relative w-32 h-20 bg-[#1a1a1a] border border-white/10 overflow-hidden flex-shrink-0">
            <img src={imgSrc} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange('')} className="absolute top-1 right-1 bg-black/70 text-white p-0.5 rounded-full hover:bg-red-600">
              <X size={12} />
            </button>
          </div>
        )}
        <div className="flex-1">
          <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="URL imagine sau incarca..." className="form-input w-full text-sm mb-2" />
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="text-xs bg-[#1a1a1a] border border-white/10 text-[#A3A3A3] hover:text-white px-3 py-1.5 flex items-center gap-1">
            <Upload size={12} /> {uploading ? 'Se incarca...' : 'Incarca imagine'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, multiline, placeholder }) => (
  <div>
    <label className="block text-[#A3A3A3] text-sm mb-2">{label}</label>
    {multiline ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} className="form-input w-full h-32 resize-none" placeholder={placeholder} />
    ) : (
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} className="form-input w-full" placeholder={placeholder} />
    )}
  </div>
);

const SaveBtn = ({ onClick, saving }) => (
  <div className="flex justify-end pt-4 border-t border-white/10 mt-6">
    <button onClick={onClick} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-page-btn">
      <Save size={18} /> {saving ? 'Se salveaza...' : 'Salveaza Modificarile'}
    </button>
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="flex items-center gap-2 mt-6 mb-4 pb-2 border-b border-white/10">
    <span className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wider">{children}</span>
  </div>
);

const AdminPages = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [homeContent, setHomeContent] = useState({});
  const [parchetContent, setParchetContent] = useState({});
  const [ceramiceContent, setCeramiceContent] = useState({});
  const [sanitareContent, setSanitareContent] = useState({});
  const [contactContent, setContactContent] = useState({});

  const DEFAULTS = {
    home: {
      hero_title: 'SHAGUN SURFACES',
      hero_subtitle: 'Placi ceramice, parchet laminat si obiecte sanitare premium pentru interioare rezidentiale si comerciale.',
      hero_image: 'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&w=1920',
      about_title: 'Despre Shagun Surfaces',
      about_p1: 'Credem in valorile de "Inovatie, Produse de Calitate si Satisfactia Clientului". Gama noastra de produse include placi ceramice, parchet laminat si obiecte sanitare.',
      about_p2: 'Aceste game isi gasesc aplicatii in complexe rezidentiale si comerciale, parcuri IT, hoteluri si restaurante, aeroporturi, spitale.',
      about_p3: 'Sustinuti de o unitate de infrastructura avansata si sofisticata, suntem bine echipati cu cele mai recente facilitati de proiectare si productie.',
      about_image: 'https://images.pexels.com/photos/276583/pexels-photo-276583.jpeg?auto=compress&w=800',
      cat_ceramice_image: 'https://images.pexels.com/photos/2117938/pexels-photo-2117938.jpeg?auto=compress&w=600',
      cat_parchet_image: 'https://images.pexels.com/photos/11262212/pexels-photo-11262212.jpeg?auto=compress&w=600',
      cat_sanitare_image: 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=600',
      consultation_title: 'Consultanta Gratuita la Fata Locului',
      consultation_text: 'Echipa noastra de specialisti vine direct la tine pentru a evalua spatiul si a-ti oferi cele mai bune recomandari.',
      consultation_image: 'https://images.pexels.com/photos/3990359/pexels-photo-3990359.jpeg?auto=compress&w=800',
    },
    parchet: {
      laminat_image: 'https://images.unsplash.com/photo-1560184897-1ee3713708ee?w=800',
      spc_image: 'https://images.pexels.com/photos/368754/pexels-photo-368754.jpeg?auto=compress&w=800',
      plinte_image: 'https://images.unsplash.com/photo-1684419269688-fe48722af68b?w=800',
      exquisit_image: 'https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800',
      amazone_image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800',
      robusto_image: 'https://images.pexels.com/photos/276583/pexels-photo-276583.jpeg?w=800',
      mammut_image: 'https://images.unsplash.com/photo-1675767443637-f35f2653b17a?w=800',
      herringbone_image: 'https://images.pexels.com/photos/129731/pexels-photo-129731.jpeg?w=800',
      advanced_image: 'https://images.unsplash.com/photo-1737100907308-67f6b49ba15f?w=800',
      progress_image: 'https://images.unsplash.com/photo-1770982726897-5e85ad0601c3?w=800',
      evolution_image: 'https://images.unsplash.com/photo-1661345699574-85a0c56a184b?w=800',
    },
    contact: {
      address: 'Str. Henri Coanda 33A, Brasov, Romania',
      phone: '0761 961 789',
      email: 'vanzari@shagunsurfaces.ro',
      schedule: 'Luni - Vineri: 09:00 - 18:00, Sambata: 09:00 - 14:00',
      whatsapp: '40761961789',
      instagram: 'https://www.instagram.com/shagunsurfaces/',
      facebook: 'https://www.facebook.com/profile.php?id=61574700722525',
    },
    ceramice: {
      hero_title: 'Placi Ceramice',
      hero_description: 'Descopera colectia noastra de gresie si faianta de inalta calitate pentru orice spatiu din casa ta.',
      hero_image: '',
    },
    sanitare: {
      hero_title: 'Obiecte Sanitare',
      hero_description: 'Completeaza-ti baia cu obiecte sanitare de calitate superioara: cazi, cabine de dus, WC-uri, lavoare si baterii elegante.',
      hero_image: '',
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [hR, pR, cR, ceR, saR] = await Promise.all([
        axios.get(`${API_URL}/api/pages/home`),
        axios.get(`${API_URL}/api/pages/parchet`),
        axios.get(`${API_URL}/api/pages/contact`),
        axios.get(`${API_URL}/api/pages/ceramice`),
        axios.get(`${API_URL}/api/pages/sanitare`)
      ]);
      setHomeContent({ ...DEFAULTS.home, ...(hR.data.content || {}) });
      setParchetContent({ ...DEFAULTS.parchet, ...(pR.data.content || {}) });
      setContactContent({ ...DEFAULTS.contact, ...(cR.data.content || {}) });
      setCeramiceContent({ ...DEFAULTS.ceramice, ...(ceR.data.content || {}) });
      setSanitareContent({ ...DEFAULTS.sanitare, ...(saR.data.content || {}) });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const getToken = () => localStorage.getItem('token');
  const save = async (page, content) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/pages/${page}`, { page, content }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Salvat cu succes!');
    } catch (e) { toast.error('Eroare la salvare'); }
    finally { setSaving(false); }
  };

  const h = (field, val) => setHomeContent(p => ({ ...p, [field]: val }));
  const pc = (field, val) => setParchetContent(p => ({ ...p, [field]: val }));
  const ct = (field, val) => setContactContent(p => ({ ...p, [field]: val }));
  const ce = (field, val) => setCeramiceContent(p => ({ ...p, [field]: val }));
  const sa = (field, val) => setSanitareContent(p => ({ ...p, [field]: val }));

  if (loading) return (
    <AdminLayout title="Editare Pagini">
      <div className="text-center py-12"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Editare Pagini">
      <div data-testid="admin-pages">
        <div className="bg-[#121212] border border-[#D4AF37]/20 p-4 mb-6 text-sm">
          <p className="text-[#D4AF37] font-semibold mb-1">CMS - Control Total Continut</p>
          <p className="text-[#A3A3A3]">Modificati textele, imaginile si continutul de pe fiecare pagina a site-ului. Schimbarile apar imediat dupa salvare.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#121212] border border-white/10 mb-6 flex-wrap">
            <TabsTrigger value="home" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Pagina Acasa</TabsTrigger>
            <TabsTrigger value="parchet" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Parchet</TabsTrigger>
            <TabsTrigger value="ceramice" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Placi Ceramice</TabsTrigger>
            <TabsTrigger value="sanitare" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Obiecte Sanitare</TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Contact</TabsTrigger>
          </TabsList>

          {/* ========== HOME ========== */}
          <TabsContent value="home">
            <div className="admin-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-[#D4AF37]" size={24} />
                <h2 className="text-white font-serif text-xl">Pagina Acasa</h2>
              </div>

              <SectionTitle>Sectiunea Hero (Banner Principal)</SectionTitle>
              <div className="space-y-4">
                <ImageUpload label="Imagine de fundal Hero" value={homeContent.hero_image} onChange={v => h('hero_image', v)} />
                <TextField label="Subtitlu Hero" value={homeContent.hero_subtitle} onChange={v => h('hero_subtitle', v)} multiline />
              </div>

              <SectionTitle>Sectiunea Despre</SectionTitle>
              <div className="space-y-4">
                <TextField label="Titlu sectiune Despre" value={homeContent.about_title} onChange={v => h('about_title', v)} />
                <TextField label="Paragraf 1" value={homeContent.about_p1} onChange={v => h('about_p1', v)} multiline />
                <TextField label="Paragraf 2" value={homeContent.about_p2} onChange={v => h('about_p2', v)} multiline />
                <TextField label="Paragraf 3" value={homeContent.about_p3} onChange={v => h('about_p3', v)} multiline />
                <ImageUpload label="Imagine sectiune Despre" value={homeContent.about_image} onChange={v => h('about_image', v)} />
              </div>

              <SectionTitle>Categorii Produse (3 carduri)</SectionTitle>
              <div className="space-y-4">
                <ImageUpload label="Imagine Placi Ceramice" value={homeContent.cat_ceramice_image} onChange={v => h('cat_ceramice_image', v)} />
                <ImageUpload label="Imagine Parchet" value={homeContent.cat_parchet_image} onChange={v => h('cat_parchet_image', v)} />
                <ImageUpload label="Imagine Obiecte Sanitare" value={homeContent.cat_sanitare_image} onChange={v => h('cat_sanitare_image', v)} />
              </div>

              <SectionTitle>Sectiunea Consultanta</SectionTitle>
              <div className="space-y-4">
                <TextField label="Titlu Consultanta" value={homeContent.consultation_title} onChange={v => h('consultation_title', v)} />
                <TextField label="Text Consultanta" value={homeContent.consultation_text} onChange={v => h('consultation_text', v)} multiline />
                <ImageUpload label="Imagine Consultanta" value={homeContent.consultation_image} onChange={v => h('consultation_image', v)} />
              </div>

              <SaveBtn onClick={() => save('home', homeContent)} saving={saving} />
            </div>
          </TabsContent>

          {/* ========== PARCHET ========== */}
          <TabsContent value="parchet">
            <div className="admin-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <Image className="text-[#D4AF37]" size={24} />
                <h2 className="text-white font-serif text-xl">Imagini Parchet</h2>
              </div>

              <SectionTitle>Categorii Principale</SectionTitle>
              <div className="space-y-4">
                <ImageUpload label="Imagine Laminat" value={parchetContent.laminat_image} onChange={v => pc('laminat_image', v)} />
                <ImageUpload label="Imagine SPC" value={parchetContent.spc_image} onChange={v => pc('spc_image', v)} />
                <ImageUpload label="Imagine Plinte" value={parchetContent.plinte_image} onChange={v => pc('plinte_image', v)} />
              </div>

              <SectionTitle>Serii Laminat</SectionTitle>
              <div className="space-y-4">
                <ImageUpload label="Kronotex Exquisit 8mm" value={parchetContent.exquisit_image} onChange={v => pc('exquisit_image', v)} />
                <ImageUpload label="Kronotex Amazone 10mm" value={parchetContent.amazone_image} onChange={v => pc('amazone_image', v)} />
                <ImageUpload label="Kronotex Robusto 12mm" value={parchetContent.robusto_image} onChange={v => pc('robusto_image', v)} />
                <ImageUpload label="Kronotex Mammut 12mm" value={parchetContent.mammut_image} onChange={v => pc('mammut_image', v)} />
                <ImageUpload label="Kronotex Herringbone 8mm" value={parchetContent.herringbone_image} onChange={v => pc('herringbone_image', v)} />
                <ImageUpload label="Superior Advanced 8mm" value={parchetContent.advanced_image} onChange={v => pc('advanced_image', v)} />
                <ImageUpload label="Superior Progress 10mm" value={parchetContent.progress_image} onChange={v => pc('progress_image', v)} />
                <ImageUpload label="Superior Evolution 12mm" value={parchetContent.evolution_image} onChange={v => pc('evolution_image', v)} />
              </div>

              <SaveBtn onClick={() => save('parchet', parchetContent)} saving={saving} />
            </div>
          </TabsContent>

          {/* ========== PLACI CERAMICE ========== */}
          <TabsContent value="ceramice">
            <div className="admin-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-[#D4AF37]" size={24} />
                <h2 className="text-white font-serif text-xl">Placi Ceramice</h2>
              </div>

              <SectionTitle>Sectiunea Hero</SectionTitle>
              <div className="space-y-4">
                <TextField label="Titlu pagina" value={ceramiceContent.hero_title} onChange={v => ce('hero_title', v)} />
                <TextField label="Descriere" value={ceramiceContent.hero_description} onChange={v => ce('hero_description', v)} multiline />
                <ImageUpload label="Imagine Hero (banner sus)" value={ceramiceContent.hero_image} onChange={v => ce('hero_image', v)} />
              </div>

              <SaveBtn onClick={() => save('ceramice', ceramiceContent)} saving={saving} />
            </div>
          </TabsContent>

          {/* ========== OBIECTE SANITARE ========== */}
          <TabsContent value="sanitare">
            <div className="admin-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-[#D4AF37]" size={24} />
                <h2 className="text-white font-serif text-xl">Obiecte Sanitare</h2>
              </div>

              <SectionTitle>Sectiunea Hero</SectionTitle>
              <div className="space-y-4">
                <TextField label="Titlu pagina" value={sanitareContent.hero_title} onChange={v => sa('hero_title', v)} />
                <TextField label="Descriere" value={sanitareContent.hero_description} onChange={v => sa('hero_description', v)} multiline />
                <ImageUpload label="Imagine Hero (banner sus)" value={sanitareContent.hero_image} onChange={v => sa('hero_image', v)} />
              </div>

              <SaveBtn onClick={() => save('sanitare', sanitareContent)} saving={saving} />
            </div>
          </TabsContent>

          {/* ========== CONTACT ========== */}
          <TabsContent value="contact">
            <div className="admin-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="text-[#D4AF37]" size={24} />
                <h2 className="text-white font-serif text-xl">Contact</h2>
              </div>
              <div className="space-y-4">
                <TextField label="Adresa" value={contactContent.address} onChange={v => ct('address', v)} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextField label="Telefon" value={contactContent.phone} onChange={v => ct('phone', v)} />
                  <TextField label="Email" value={contactContent.email} onChange={v => ct('email', v)} />
                </div>
                <TextField label="Program" value={contactContent.schedule} onChange={v => ct('schedule', v)} />
                <TextField label="WhatsApp (nr. international, ex: 40761961789)" value={contactContent.whatsapp} onChange={v => ct('whatsapp', v)} />
                <TextField label="Link Instagram" value={contactContent.instagram} onChange={v => ct('instagram', v)} />
                <TextField label="Link Facebook" value={contactContent.facebook} onChange={v => ct('facebook', v)} />
              </div>
              <SaveBtn onClick={() => save('contact', contactContent)} saving={saving} />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminPages;
